import * as styles from "./ContentView.styles.ts";
import { useRef } from "react";
import { ProductCollection } from "../ProductCollection/ProductCollection.tsx";
import { NoRowsOverlay } from "../NoRowsOverlay/NoRowsOverlay.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";
import { useExonnHubOrder } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubOrder.ts";
import { useIsAddingProductToCart } from "src/admin/modules/b2b-gate-order/components/EditSupplierOrderForB2bGate/hooks/useIsAddingProductToCart.ts";
import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { useExonnHubStore } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubStore.ts";
import { observer } from "mobx-react-lite";
import { useExonnHubCatalogParams } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubCatalogParams.ts";
import { useExonnHubCatalog } from "src/admin/modules/b2b-gate-order/components/ExonnHub/hooks/useExonnHubCatalog.tsx";
import { keepPreviousData } from "@tanstack/react-query";
import { EmptyState } from "src/components/EmptyState/EmptyState.tsx";
import { ProductPanel } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/ProductPanel/ProductPanel.tsx";
import { useExonnHubContext } from "src/admin/modules/b2b-gate-order/components/ExonnHub/useExonnHubContext.ts";
import { AppliedFilters } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/ContentView/AppliedFilters/AppliedFilters.tsx";
import { Breadcrumbs } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/ContentView/Breadcrumbs/Breadcrumbs.tsx";
import { Header } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/ContentView/Header/Header.tsx";
import { SubHeader } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/ContentView/SubHeader/SubHeader.tsx";
import { useIsDemoShop } from "src/hooks/use-is-demo-shop.ts";
import { DemoShopWarning } from "src/admin/modules/b2b-gate-order/components/ExonnHub/components/DemoShopWarning/DemoShopWarning.tsx";

export const ContentView = observer(() => {
  const store = useExonnHubStore();

  const { orderId } = useExonnHubContext();
  const { refetch: refetchOrder } = useExonnHubOrder({
    orderId,
  });

  const listParams = useExonnHubCatalogParams();

  const {
    data: catalogData,
    refetch: refetchCatalogData,
    isFetching: isCatalogDataFetching,
    error: catalogError,
  } = useExonnHubCatalog(listParams, {
    placeholderData: keepPreviousData,
  });

  const isDemoShop = useIsDemoShop();

  const rowCount = catalogData?.meta?.total || 0;

  const isAddingProductToCart = useIsAddingProductToCart();

  const { search } = store;

  const { t } = useTranslation(TranslationNamespaceEnum.ExonnHubComponent);
  const productContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolled = store.hasScrolledItems;

  const refresh = () => {
    refetchOrder();
    refetchCatalogData();
  };

  const handleChangePage = () => {
    if (productContainerRef.current) {
      productContainerRef.current.scrollTop = 0;
    }
  };

  return (
    <div css={styles.root}>
      <LoadingOverlay.Root
        isLoading={
          (isAddingProductToCart && !store.isOrderPreviewOpen) ||
          isCatalogDataFetching
        }
      >
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>
          {isAddingProductToCart ? t("updatingOrder") : t("loadingCatalog")}
        </LoadingOverlay.Title>
      </LoadingOverlay.Root>
      <Header onRefresh={refresh} />
      <Breadcrumbs />
      <div css={styles.subHeaderContainer({ hasScrolled })}>
        <SubHeader onChangePage={handleChangePage} />

        <AppliedFilters />
        {isDemoShop && <DemoShopWarning />}
      </div>

      {catalogError ? (
        <EmptyState
          title={t("errors.loadProductsFailure.title")}
          type={"warning"}
          description={t("errors.loadProductsFailure.description")}
          action={
            <ButtonV2
              variant={"filled"}
              onClick={() => {
                // reset sorting, it might be the reason why request failed
                store.setSort(undefined);
                refetchCatalogData();
              }}
            >
              {t("errors.loadProductsFailure.action")}
            </ButtonV2>
          }
        />
      ) : rowCount > 0 ? (
        <div
          ref={productContainerRef}
          css={styles.body({ hasScrolled })}
          onScroll={(e) =>
            store.setHasScrolledItems(e.currentTarget.scrollTop > 0)
          }
        >
          <ProductCollection />
          <ProductPanel />
        </div>
      ) : (
        <NoRowsOverlay hasFiltersApplied={!!search} />
      )}
    </div>
  );
});
